<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { parseInterval } from '@/common/utils/core/time/parse';
import { useServerTime } from '@/common/composables/serverTime/useServerTime';
import { resolveIntersectionVisibility } from '../utils';

interface Props {
  /** 일시정지 여부 */
  isPaused?: boolean;
  /** 갱신 주기 (예: 'p5s', 'I5s') */
  interval?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
}

interface Emits {
  (e: 'tick', serverTime: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  isPaused: false,
  interval: 'p5s',
  disabled: false,
});

const emit = defineEmits<Emits>();

const wrapperRef = ref<HTMLElement | null>(null);

// ============================================================
// Server Time Management
// ============================================================

const intervalMs = computed(() => {
  const { offset, unit } = parseInterval(props.interval);
  return dayjs.duration(offset, unit).asMilliseconds();
});

// 같은 LiveUpdater 인스턴스에서 짧은 시간 안에 동일 serverTime 으로 tick 이 두 번 발사되는
// 케이스(예: 다중 watcher 동시 발동, useServerTime.start 의 자동 onTick + 다른 외부 trigger)
// 의 redundant emit 을 차단하는 안전망. 초 단위 포맷이라 같은 초 안 중복은 정확히 잡힌다.
let lastEmittedTime = '';

const handleTick = (serverTime: string) => {
  if (props.isPaused || props.disabled) return;
  if (serverTime === lastEmittedTime) return;
  lastEmittedTime = serverTime;
  emit('tick', serverTime);
};

const { currentTime, isRunning, start, stop } = useServerTime(
  {
    interval: intervalMs.value,
    pauseOnHidden: false,
  },
  handleTick,
);

// ============================================================
// Worker Control Wrappers
// ============================================================

const startWorker = async () => {
  if (props.disabled) return;
  await start(intervalMs.value);
};

const stopWorker = () => {
  stop();
};

// ============================================================
// Visibility Management (브라우저 탭 + 앱 내부 탭 통합)
// ============================================================

let visibilityObserver: IntersectionObserver | null = null;
let observedElement: Element | null = null;
let isComponentVisible = true;
let hasBeenHiddenOnce = false;

const onVisibilityChange = (visible: boolean) => {
  if (visible === isComponentVisible) return;
  isComponentVisible = visible;

  if (!visible) {
    hasBeenHiddenOnce = true;
    stopWorker();
    return;
  }

  if (hasBeenHiddenOnce && !props.isPaused && !props.disabled) {
    void startWorker();
  }
};

const handleDocumentVisibility = () => {
  if (document.visibilityState === 'hidden') {
    onVisibilityChange(false);
    return;
  }
  // 브라우저 탭 복귀: element가 실제로 보이는지 확인 (숨겨진 앱 탭일 수 있음)
  if (!observedElement) return;
  const { width, height } = observedElement.getBoundingClientRect();
  onVisibilityChange(width > 0 && height > 0);
};

// ============================================================
// Lifecycle
// ============================================================

onMounted(async () => {
  if (!props.isPaused && !props.disabled) {
    await startWorker();
  }

  const parentEl = wrapperRef.value?.parentElement;
  if (!(parentEl instanceof Element)) return;

  observedElement = parentEl;

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      const visibility = resolveIntersectionVisibility(entry);
      if (visibility !== null) onVisibilityChange(visibility);
    },
    { threshold: 0 },
  );
  visibilityObserver.observe(parentEl);

  document.addEventListener('visibilitychange', handleDocumentVisibility);
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleDocumentVisibility);
  visibilityObserver?.disconnect();
});

// ============================================================
// Watchers
// ============================================================

watch(
  () => props.interval,
  async (curr, prev) => {
    const currParsed = parseInterval(curr);
    const prevParsed = parseInterval(prev);

    if (!props.isPaused && !isEqual(currParsed, prevParsed) && !props.disabled) {
      await startWorker();
    }
  },
);

watch(
  () => props.isPaused,
  async (isPaused) => {
    if (props.disabled) return;

    if (isPaused) {
      stopWorker();
    } else {
      await startWorker();
    }
  },
);

watch(
  () => props.disabled,
  async (disabled) => {
    if (disabled) {
      stopWorker();
    } else if (!props.isPaused) {
      await startWorker();
    }
  },
);

// ============================================================
// Expose
// ============================================================

defineExpose({
  start: startWorker,
  stop: stopWorker,
  isRunning,
  currentTime,
});
</script>

<template>
  <span
    ref="wrapperRef"
    style="display: contents"
  />
</template>
